import React, { useState, useEffect, useReducer } from "react";
import { getCachedWorkers, useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import {
  useDispoAtelierStore,
  ZONE_COLORS,
  ZONE_TYPES,
} from "@store/dispoAtelierStore";
import type { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import DropDown from "@components/ui/dropdown/DropDown";
import SearchField from "@components/ui/textfields/SearchField";
import MiniCalendar from "@components/ui/MiniCalendar";
import { WeekTimeGrid } from "./WeekTimeGrid";
import { DayTimeGrid } from "./DayTimeGrid";
import { BlockModal } from "./BlockModal";

const ZONE_FILTER_OPTIONS: { value: ZoneType | "all"; label: string }[] = [
  { value: "all", label: "Toutes les zones" },
  ...ZONE_TYPES.map((z) => ({ value: z, label: ZONE_COLORS[z].label })),
];

export function DispoAtelier() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchBlocks = useDispoAtelierStore((s) => s.fetchBlocks);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!orgData?.id) return;
    fetchBlocks(orgData.id);
    if (getCachedWorkers().length === 0) {
      useAtelierStore.getState().fetchData(orgData.id).then(forceUpdate);
    }
  }, [orgData?.id]);

  const selectedDate = useDispoAtelierStore((s) => s.selectedDate);
  const setSelectedDate = useDispoAtelierStore((s) => s.setSelectedDate);
  const viewMode = useDispoAtelierStore((s) => s.viewMode);
  const setViewMode = useDispoAtelierStore((s) => s.setViewMode);
  const searchQuery = useDispoAtelierStore((s) => s.searchQuery);
  const setSearchQuery = useDispoAtelierStore((s) => s.setSearchQuery);
  const activeZoneFilter = useDispoAtelierStore((s) => s.activeZoneFilter);
  const setActiveZoneFilter = useDispoAtelierStore((s) => s.setActiveZoneFilter);
  const selectedWorkerIds = useDispoAtelierStore((s) => s.selectedWorkerIds);
  const toggleWorkerSelection = useDispoAtelierStore((s) => s.toggleWorkerSelection);
  const selectAllWorkers = useDispoAtelierStore((s) => s.selectAllWorkers);
  const deselectAllWorkers = useDispoAtelierStore((s) => s.deselectAllWorkers);
  const getSummaryByZone = useDispoAtelierStore((s) => s.getSummaryByZone);

  const [editingBlock, setEditingBlock] = useState<AvailabilityBlock | null>(null);

  const zoneSummary = getSummaryByZone(selectedWorkerIds);

  return (
    <div
      className="flex flex-col h-screen overflow-auto"
      style={{ background: "#FAFAFA" }}
    >
      <div className="flex flex-col h-fit">
        {/* Header */}
        <div
          className="flex flex-col"
          style={{ gap: 4, padding: "30px 40px 0 40px" }}
        >
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "30px",
              color: "#000",
            }}
          >
            Dispo atelier
          </span>
          <span
            className="text-sm font-medium"
            style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
          >
            Gérez les disponibilités de vos équipes atelier.
          </span>
        </div>

        {/* Worker selector — unified multi-select with shadow pills */}
        <div
          className="flex flex-row flex-wrap gap-1 items-center"
          style={{ padding: "16px 40px 0 40px" }}
        >
          <button
            onClick={() =>
              selectedWorkerIds.length === getCachedWorkers().length
                ? deselectAllWorkers()
                : selectAllWorkers()
            }
            className="px-4 py-2 text-sm font-medium rounded-lg transition-all"
            style={{
              fontFamily: "Poppins",
              ...(selectedWorkerIds.length === getCachedWorkers().length
                ? {
                    background: "#fff",
                    color: "#000",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.12)",
                    border: "1px solid #E5E5E5",
                  }
                : {
                    background: "#F0F0F0",
                    color: "#A1A1A1",
                    boxShadow: "inset 0px 2px 4px rgba(0,0,0,0.08)",
                    border: "1px solid transparent",
                  }),
            }}
          >
            Tous
          </button>
          {getCachedWorkers().map((w) => {
            const isSelected = selectedWorkerIds.includes(w.id);
            return (
              <button
                key={w.id}
                onClick={() => toggleWorkerSelection(w.id)}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-all"
                style={{
                  fontFamily: "Poppins",
                  ...(isSelected
                    ? {
                        background: "#fff",
                        color: "#000",
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.12)",
                        border: "1px solid #E5E5E5",
                      }
                    : {
                        background: "#F0F0F0",
                        color: "#A1A1A1",
                        boxShadow: "inset 0px 2px 4px rgba(0,0,0,0.08)",
                        border: "1px solid transparent",
                      }),
                }}
              >
                {w.name}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div
          className="flex flex-row flex-1"
          style={{
            gap: 24,
            padding: "16px 30px 0 30px",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Left sidebar */}
          <div
            className="flex flex-col flex-shrink-0"
            style={{
              width: 196,
              gap: 20,
              overflowY: "auto",
              paddingBottom: 30,
            }}
          >
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              weekHighlight={viewMode === "semaine"}
              showNavButtons
            />

            {/* Zone summary (week view only) */}
            {zoneSummary.length > 0 && (
              <div className="flex flex-col" style={{ gap: 6 }}>
                <span
                  className="text-h4 font-medium"
                  style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
                >
                  Résumé par zone
                </span>
                {zoneSummary.map((s) => {
                  const colors = ZONE_COLORS[s.zoneType];
                  return (
                    <div
                      key={s.zoneType}
                      className="flex flex-row justify-between items-center py-1.5 px-2 rounded-lg"
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <span
                        className="text-h5 font-medium"
                        style={{ color: colors.text }}
                      >
                        {s.label}
                      </span>
                      <span
                        className="text-h5 font-medium"
                        style={{ color: colors.text, opacity: 0.7 }}
                      >
                        {s.totalHours.toFixed(1)}h
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Zone legend */}
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span
                className="text-h4 font-medium"
                style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
              >
                Légende
              </span>
              {ZONE_TYPES.map((z) => {
                const colors = ZONE_COLORS[z];
                return (
                  <div
                    key={z}
                    className="flex flex-row items-center"
                    style={{ gap: 8 }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                      }}
                    />
                    <span
                      className="text-h4 font-medium"
                      style={{ fontFamily: "Poppins", color: "#737373" }}
                    >
                      {colors.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right content */}
          <div
            className="flex flex-col flex-1 overflow-hidden"
            style={{ gap: 10, minWidth: 0 }}
          >
            {/* Search + zone filter + view toggle */}
            <div
              className="flex flex-row items-center"
              style={{ gap: 16 }}
            >
              <div style={{ width: 220 }}>
                <SearchField
                  value={searchQuery}
                  onChange={(v: string) => setSearchQuery(v)}
                  placeholder="Chercher .."
                />
              </div>
              <div style={{ width: 180 }}>
                <DropDown
                  options={ZONE_FILTER_OPTIONS}
                  value={activeZoneFilter}
                  onSelect={(v) => setActiveZoneFilter(v as ZoneType | "all")}
                  variant="small"
                />
              </div>
              <div style={{ width: 180 }}>
                <ChunkyTabs
                  activeTab={viewMode}
                  tabs={[
                    { value: "jour", label: "Jour" },
                    { value: "semaine", label: "Semaine" },
                  ]}
                  setActiveTab={(tab) =>
                    setViewMode(tab as "jour" | "semaine")
                  }
                />
              </div>
            </div>

            {/* Time grid */}
            <div
              className="flex flex-col flex-1 overflow-hidden rounded-lg"
              style={{
                border: "1px solid #E5E5E5",
                background: "#fff",
              }}
            >
              {viewMode === "semaine" ? (
                <WeekTimeGrid onEditBlock={setEditingBlock} />
              ) : (
                <DayTimeGrid onEditBlock={setEditingBlock} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editingBlock && (
        <BlockModal
          mode="edit"
          block={editingBlock}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
}
